import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, ArrowLeft, Activity, Home, Wrench, Inbox } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { NotificationBell } from "../components/site/NotificationBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

const KIND_META = {
  lead: { icon: Home, chip: "bg-[#EAF7FC] text-[#133955]", label: "Project" },
  repair: { icon: Wrench, chip: "bg-[#FFE8B0] text-[#7a5b00]", label: "Repair" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "lead", label: "Projects" },
  { key: "repair", label: "Repairs" },
];

export default function AdminActivity() {
  const { token, role, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!authLoading && !token) navigate("/admin/login");
    if (!authLoading && token && role && role !== "admin") navigate(role === "agent" ? "/agent" : "/portal");
  }, [authLoading, token, role, navigate]);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/activity`, { headers: { Authorization: `Bearer ${token}` } });
    setEvents(data.events || []);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => { try { await load(); } catch (e) { if (e?.response?.status === 401) { logout(); navigate("/admin/login"); } } finally { setLoading(false); } })();
  }, [token, load, logout, navigate]);

  if (authLoading || loading) return <div className="min-h-screen grid place-items-center bg-[#EAF7FC]"><Loader2 className="animate-spin text-[#133955]" size={32} /></div>;

  const visible = filter === "all" ? events : events.filter((e) => e.kind === filter);
  const counts = { all: events.length, lead: events.filter((e) => e.kind === "lead").length, repair: events.filter((e) => e.kind === "repair").length };

  const openEvent = (e) => {
    if (e.kind === "lead" && e.lead_id) navigate(`/admin?lead=${e.lead_id}`);
    else if (e.kind === "repair" && e.request_id) navigate(`/admin/repair-requests?repair=${e.request_id}`);
  };

  return (
    <div className="min-h-screen bg-[#EAF7FC]" data-testid="admin-activity">
      <header className="bg-white border-b border-[rgba(19,57,85,0.08)] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button data-testid="nav-back-admin" onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold text-[#133955] hover:bg-[#EAF7FC]"><ArrowLeft size={16} /> Project Requests</button>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white"><LogOut size={16} /> Sign Out</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-center gap-3 mb-1">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#133955] text-white"><Activity size={22} /></span>
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#133955] tracking-tight">Activity Log</h1>
            <p className="text-[#173042]/70" data-testid="activity-count">{visible.length} {filter === "lead" ? "project" : filter === "repair" ? "repair" : ""} event{visible.length === 1 ? "" : "s"}{filter === "all" ? " across projects and repair requests" : ""}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-5 mb-6" data-testid="activity-filters">
          {FILTERS.map((f) => (
            <button key={f.key} data-testid={`activity-filter-${f.key}`} onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${filter === f.key ? "bg-[#133955] text-white" : "bg-white text-[#133955] border border-[rgba(19,57,85,0.12)] hover:bg-[#EAF7FC]"}`}>
              {f.label}
              <span className={`rounded-full px-2 py-0.5 text-xs ${filter === f.key ? "bg-white/20 text-white" : "bg-[#EAF7FC] text-[#133955]"}`}>{counts[f.key]}</span>
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <div className="text-center py-20 rounded-2xl bg-white border border-[rgba(19,57,85,0.08)]" data-testid="no-activity">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#EAF7FC] text-[#54BCE6] mx-auto mb-4"><Inbox size={26} /></span>
            <p className="text-[#173042] font-semibold">No activity yet</p>
            <p className="text-[#173042]/60 text-sm mt-1">Lead and repair-request events will appear here as work happens.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="activity-list">
            {visible.map((e) => {
              const meta = KIND_META[e.kind] || KIND_META.lead;
              const Icon = meta.icon;
              const clickable = (e.kind === "lead" && e.lead_id) || (e.kind === "repair" && e.request_id);
              return (
                <button key={e.id} data-testid={`activity-item-${e.id}`} onClick={() => clickable && openEvent(e)}
                  className={`w-full text-left rounded-xl bg-white border border-[rgba(19,57,85,0.08)] px-4 py-3 flex items-start gap-3 transition-colors ${clickable ? "hover:border-[#54BCE6] cursor-pointer" : "cursor-default"}`}>
                  <span className={`mt-0.5 grid place-items-center w-8 h-8 rounded-lg shrink-0 ${meta.chip}`}><Icon size={15} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold text-[#54BCE6]">{e.ref}</span>
                      <span className="text-xs font-semibold text-[#173042]/60 truncate">{e.label}</span>
                      {e.action === "update_required" && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-[#FBD5D5] text-[#9b1c1c]">UPDATE REQUIRED</span>}
                    </div>
                    <p className="text-sm text-[#173042] mt-0.5">{e.description}</p>
                    <p className="text-xs text-[#173042]/45 mt-0.5">{e.actor_role === "system" ? "System" : e.actor_email} · {fmt(e.created_at)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
